/** Save a basket, and reopen one.
 *
 * Loading a save moves the whole page to that session and expiry, not
 * just the legs — a basket priced against a different day would be
 * meaningless, and silently keeping the current date is exactly the kind
 * of wrong-but-plausible state this app has been bitten by before.
 */

import { useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { describeError } from "@/lib/http";
import { simulatorApi, type SimLeg, type SimSaved } from "@/lib/simulatorApi";

const KEY = ["simulator-saves"];

interface Props {
  underlying: string;
  tradeDate: string;
  expiry: string | null;
  legs: SimLeg[];
  onLoad: (saved: SimSaved) => void;
}

function SaveForm({
  underlying,
  tradeDate,
  expiry,
  legs,
}: Readonly<Omit<Props, "onLoad">>): ReactElement {
  const [name, setName] = useState("");
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: () =>
      simulatorApi.saves.create({ name, underlying, tradeDate, expiry: expiry ?? "", legs }),
    onSuccess: () => {
      setName("");
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
  return (
    <div className="flex flex-wrap items-end gap-2">
      <div>
        <label className="label" htmlFor="sim-save-name">
          Save this basket as
        </label>
        <input
          id="sim-save-name"
          className="input w-64"
          value={name}
          placeholder="short straddle, 28 Aug"
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
      </div>
      <button
        type="button"
        className="btn btn-primary px-3 py-1.5 text-xs"
        disabled={name.trim() === "" || legs.length === 0 || save.isPending}
        onClick={() => {
          save.mutate();
        }}
      >
        Save
      </button>
      {save.isError && <span className="text-xs text-danger">{describeError(save.error)}</span>}
    </div>
  );
}

function SavedRow({
  saved,
  onLoad,
}: Readonly<{ saved: SimSaved; onLoad: (s: SimSaved) => void }>): ReactElement {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: () => simulatorApi.saves.remove(saved.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
  return (
    <li className="flex items-center justify-between gap-3 py-1.5">
      <button
        type="button"
        className="text-left text-sm text-accent hover:underline"
        onClick={() => {
          onLoad(saved);
        }}
      >
        {saved.name}
      </button>
      <span className="font-mono text-[11px] text-ink-muted">
        {saved.underlying} · {saved.trade_date} · {saved.legs.length} legs
      </span>
      <button
        type="button"
        className="btn btn-ghost px-2 py-0.5 text-xs"
        aria-label={`Delete ${saved.name}`}
        onClick={() => {
          remove.mutate();
        }}
      >
        ×
      </button>
    </li>
  );
}

export function SavedBaskets(props: Readonly<Props>): ReactElement {
  const saves = useQuery({ queryKey: KEY, queryFn: () => simulatorApi.saves.list() });
  return (
    <details className="border-t border-line pt-3">
      <summary className="cursor-pointer text-sm font-medium text-ink">
        Saved baskets{saves.data ? ` (${String(saves.data.length)})` : ""}
      </summary>
      <div className="mt-3 flex flex-col gap-3">
        <SaveForm
          underlying={props.underlying}
          tradeDate={props.tradeDate}
          expiry={props.expiry}
          legs={props.legs}
        />
        {saves.data && saves.data.length > 0 && (
          <ul className="divide-y divide-line">
            {saves.data.map((saved) => (
              <SavedRow key={saved.id} saved={saved} onLoad={props.onLoad} />
            ))}
          </ul>
        )}
        {saves.data?.length === 0 && (
          <p className="text-xs text-ink-muted">
            Nothing saved yet. A saved basket reopens on its own session and expiry.
          </p>
        )}
      </div>
    </details>
  );
}
